/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { DeploymentActionContainer, DeploymentActionResource, DeploymentStepResource, GitRef, ProcessResource, ProjectResource, ProjectTemplateDetailsResource, RunbookResource } from "@octopusdeploy/octopus-server-client";
import { ProcessType } from "@octopusdeploy/octopus-server-client";
import { exhaustiveCheck } from "@octopusdeploy/type-utils";
import type { ReactNode } from "react";
import type { ConsumerProcessTemplateEditorTabs } from "~/areas/projects/components/Process/ProcessTemplates/ProcessTemplateUsageDetails";
import type { IQuery } from "~/components/QueryStringFilters/QueryStringFilters";
import type { ActionPlugin } from "../../../../components/Actions/pluginRegistry";

export interface ProcessPageSupportedActions {
    refreshLookupData: () => Promise<void>;
    refreshVariables?: () => Promise<void>;
}

export interface AssembledAction {
    step: StoredStep;
    action: StoredAction | null;
    actionTypeName: string;
    pageTitle: string;
}

export interface AssembleParentStepResult {
    step: StoredStep;
    pageTitle: string;
}

export enum EnvironmentOption {
    All = "all",
    Include = "include",
    Exclude = "exclude",
}

export enum ExecutionLocation {
    OctopusServer = "OctopusServer",
    WorkerPool = "WorkerPool",
    WorkerPoolForRoles = "WorkerPoolForRoles",
    OctopusServerForRoles = "OctopusServerForRoles",
    DeploymentTarget = "DeploymentTarget",
}

export type RunOnServerOrWorkerPool = RunOnBuiltInWorker | RunOnWorkerPool;
export type RunOn = RunOnDeploymentTarget | RunOnServerOrWorkerPool;

export class RunOnDeploymentTarget implements RunOnModel {
    executionLocation = ExecutionLocation.DeploymentTarget;
}

export class RunOnBuiltInWorker implements RunOnModel {
    executionLocation: ExecutionLocation.OctopusServer | ExecutionLocation.OctopusServerForRoles = undefined!;
    runningInContainer: boolean = undefined!;
    container: DeploymentActionContainer = undefined!;
}

export class RunOnWorkerPool implements RunOnModel {
    executionLocation: ExecutionLocation.WorkerPool | ExecutionLocation.WorkerPoolForRoles = undefined!;
    runningInContainer: boolean = undefined!;
    container: DeploymentActionContainer = undefined!;
}

export enum TargetRoles {
    Optional,
    None,
    Required,
}

export interface RunOnModel {
    executionLocation: ExecutionLocation;
}

export interface ProcessFilter {
    actionId?: string;
    actionType?: string;
    reloadKey?: string;
    stepTemplates?: string;
    childStepTemplates?: string;
    templateId?: string;
    templateSpaceId?: string;
    isCommunityTemplate?: "true" | "false";
    parentStepId?: string;
    processTemplateSlug?: string;
    processTemplateVersionMask?: string;
    new?: string;
    actionCategory?: string;
    tags?: string[];
    gitRef?: string;
    activeTab?: ConsumerProcessTemplateEditorTabs;
}

export interface ProcessQuery extends IQuery, ProcessFilter {}

export type ByIdLookup<TModel> = { byId: { [id: string]: TModel } };
export type AllIds = { allIds: string[] };

type IdAndIsProcessTemplate = {
    id: string;
    isProcessTemplate: boolean;
};

export type AllIdAndIsProcessTemplates = { allIds: IdAndIsProcessTemplate[] };

export interface LookupState<T> {
    [key: string]: T;
}

export interface StoredErrors {
    steps: { [id: string]: { [key: string]: string } };
    actions: {
        [id: string]: { [key: string]: string };
    };
    processTemplateUsages: { [id: string]: { [key: string]: string } };
    processTemplateSteps: {
        [processTemplateStepName: string]: { [stepIndex: number]: { [key: string]: string } };
    };
    processTemplateActions: {
        [processTemplateStepName: string]: { [stepIndex: number]: { [actionIndex: number]: { [key: string]: string } } };
    };
    global: { [key: string]: string };
    globalMessage: string;
}

export type ActionError = {
    key: string;
    stepId: string;
    actionId: string;
    value: string;
};

export type StepError = {
    key: string;
    stepId: string;
    value: string;
};

export type ProcessTemplateUsageError = {
    key: string;
    processTemplateUsageId: string;
    value: string;
};

export type ProcessTemplateActionError = {
    key: string;
    processTemplateStepName: string;
    stepIndex: number;
    actionIndex: number;
    value: string;
};

export type ProcessTemplateStepError = {
    key: string;
    processTemplateStepName: string;
    stepIndex: number;
    value: string;
};

export type GlobalError = {
    key: string;
    value: string;
};

export type ErrorValuePair = {
    key: string;
    value: string;
};

export type ProcessError = ProcessTemplateActionError | ProcessTemplateStepError | ActionError | StepError | ProcessTemplateUsageError | GlobalError;

export type StoredStep = Omit<DeploymentStepResource, "Actions"> & { ActionIds: string[] };
export interface StoredAction extends DeploymentActionResource {
    ParentId: string;
    plugin: ActionPlugin;
}

export type ProcessState = Omit<ProcessResource, "Steps"> | null;
export type StepsState = ByIdLookup<StoredStep> & AllIdAndIsProcessTemplates;

export type ActionsState = ByIdLookup<StoredAction> & AllIds;

export type StoredModelState = {
    process: ProcessState;
    actions: ActionsState;
    steps: StepsState;
};

export type MergeModelState = {
    staged: StoredModelState;
    server: StoredModelState;
    processMerged: boolean;
    dialogClosed: boolean;
};

export type ProcessContextModelState = {
    model: StoredModelState;
    cleanModel: StoredModelState;
    mergeModel: MergeModelState;
    processIdentifier: ProcessIdentifier;
    allowTemplatedProjectProcessEditorView: boolean;
};

export type DeploymentProcessIdentifier = {
    type: ProcessType.Deployment;
    id: string;
    projectId: string;
    projectSlug: string;
    spaceId: string;
};

export type RunbookProcessIdentifier = {
    type: ProcessType.Runbook;
    id: string;
    runbookId: string;
    runbookName: string;
    projectId: string;
    projectSlug: string;
    spaceId: string;
};

export type ProcessTemplateProcessIdentifier = {
    type: ProcessType.ProcessTemplate;
    id: string;
    processTemplateId: string;
    slug: string;
    gitRef: GitRef;
};

export type TemplatedProjectProcessIdentifier = {
    type: ProcessType.TemplatedProject;
    id: string;
    projectTemplateSlug: string;
    version: string;
    projectId: string;
    projectSlug: string;
    spaceId: string;
};

export type ProjectTemplateProcessIdentifier = {
    type: ProcessType.ProjectTemplate;
    id: string;
    slug: string;
    gitRef: GitRef;
};

export type ProcessIdentifier = DeploymentProcessIdentifier | RunbookProcessIdentifier | ProcessTemplateProcessIdentifier | TemplatedProjectProcessIdentifier | ProjectTemplateProcessIdentifier;

export function isDeploymentProcessIdentifier(processIdentifier: ProcessIdentifier | undefined): processIdentifier is DeploymentProcessIdentifier {
    return processIdentifier ? processIdentifier.type === ProcessType.Deployment : false;
}

export function isRunbookProcessIdentifier(processIdentifier: ProcessIdentifier | undefined): processIdentifier is RunbookProcessIdentifier {
    return processIdentifier ? processIdentifier.type === ProcessType.Runbook : false;
}

export function isDeploymentOrRunbookProcessIdentifier(processIdentifier: ProcessIdentifier | undefined): processIdentifier is DeploymentProcessIdentifier | RunbookProcessIdentifier {
    return isDeploymentProcessIdentifier(processIdentifier) || isRunbookProcessIdentifier(processIdentifier);
}

export function isProcessTemplateProcessIdentifier(processIdentifier: ProcessIdentifier | undefined): processIdentifier is ProcessTemplateProcessIdentifier {
    return processIdentifier ? processIdentifier.type === ProcessType.ProcessTemplate : false;
}

export function isProcessOrProjectTemplateProcessIdentifier(processIdentifier: ProcessIdentifier | undefined): processIdentifier is ProcessTemplateProcessIdentifier | ProjectTemplateProcessIdentifier {
    return isProcessTemplateProcessIdentifier(processIdentifier) || isProjectTemplateProcessIdentifier(processIdentifier);
}

export function isProcessOrProjectTemplateProcessType(processType: ProcessType | undefined): boolean {
    return processType === ProcessType.ProcessTemplate || processType === ProcessType.ProjectTemplate;
}

// TODO: we'll have to handle templated runbook processes in the near future.
export function isTemplatedProjectProcessIdentifier(processIdentifier: ProcessIdentifier | undefined): processIdentifier is TemplatedProjectProcessIdentifier {
    return processIdentifier ? processIdentifier.type === ProcessType.TemplatedProject : false;
}

export function isProjectTemplateProcessIdentifier(processIdentifier: ProcessIdentifier | undefined): processIdentifier is ProjectTemplateProcessIdentifier {
    return processIdentifier ? processIdentifier.type === ProcessType.ProjectTemplate : false;
}

export function getProcessTemplateConsumerResourceType(
    processIdentifier: DeploymentProcessIdentifier | RunbookProcessIdentifier | ProjectTemplateProcessIdentifier | TemplatedProjectProcessIdentifier
): "Deployment Process" | "Runbook" | "Project Template" | "Templated Project" {
    switch (processIdentifier.type) {
        case ProcessType.Deployment:
            return "Deployment Process";
        case ProcessType.Runbook:
            return "Runbook";
        case ProcessType.ProjectTemplate:
            return "Project Template";
        case ProcessType.TemplatedProject:
            return "Templated Project";
        default:
            return exhaustiveCheck(processIdentifier, `Unhandled process type: ${JSON.stringify(processIdentifier)}`);
    }
}

export function toDeploymentProcessIdentifier(project: ProjectResource): DeploymentProcessIdentifier {
    return {
        type: ProcessType.Deployment,
        id: project.DeploymentProcessId,
        projectId: project.Id,
        projectSlug: project.Slug,
        spaceId: project.SpaceId,
    };
}

export function toRunbookProcessIdentifier(project: ProjectResource, runbook: RunbookResource): RunbookProcessIdentifier {
    return {
        type: ProcessType.Runbook,
        id: runbook.RunbookProcessId,
        runbookId: runbook.Id,
        runbookName: runbook.Name,
        projectId: project.Id,
        projectSlug: project.Slug,
        spaceId: project.SpaceId,
    };
}

export function toProcessTemplateProcessIdentifier(slug: string, gitRef: GitRef): ProcessTemplateProcessIdentifier {
    const processTemplateId = `${gitRef}|${slug}`;
    return {
        type: ProcessType.ProcessTemplate,
        id: `process-${processTemplateId}`, // Unused for now
        processTemplateId,
        slug,
        gitRef: gitRef,
    };
}

export function toTemplatedProjectProcessIdentifier(project: ProjectResource, projectTemplateDetails: ProjectTemplateDetailsResource): TemplatedProjectProcessIdentifier {
    const projectTemplateId = `${projectTemplateDetails.Slug}|${projectTemplateDetails.Version}`;
    return {
        type: ProcessType.TemplatedProject,
        id: `templated-project-${projectTemplateId}`, // Unused for now
        projectTemplateSlug: projectTemplateDetails.Slug,
        version: projectTemplateDetails.Version,
        projectId: project.Id,
        projectSlug: project.Slug,
        spaceId: project.SpaceId,
    };
}

export function toProjectTemplateProcessIdentifier(slug: string, gitRef: GitRef): ProjectTemplateProcessIdentifier {
    const projectTemplateId = `${gitRef}|${slug}`;
    return {
        type: ProcessType.ProjectTemplate,
        id: `process-${projectTemplateId}`, // Unused for now
        slug,
        gitRef: gitRef,
    };
}

export type MetaStepProperties = Omit<StoredStep, "Properties" | "Actions" | "Id">;
export type MetaActionProperties = Omit<StoredAction, "Properties" | "Id" | "ActionType" | "Links">;

export type SelectorType<TTarget extends { [key: string]: (...args: never[]) => unknown }> = {
    [K in keyof TTarget]: ReturnType<TTarget[K]>;
};

//#region Warning guidance / validation

export interface Warnings {
    message?: string;
    parsedHelpLinks?: string[];
    helpText?: string;
    helpLink?: string;
    fieldWarnings: {
        [other: string]: string;
    };
    details?:
        | {
              [key: string]: string | ReactNode;
          }
        | StepWarningDetails;
}

export interface StoredWarnings {
    steps: { [id: string]: { [key: string]: string | ReactNode } | StepWarningDetails };
    actions: {
        [id: string]: { [key: string]: string | ReactNode };
    };
    global: { [key: string]: string | ReactNode };
    globalMessage?: string;
}

export type ActionWarning = {
    key: string;
    stepId: string;
    actionId: string;
    value: string | ReactNode;
};

export type ActionWarningWithDetails = {
    key: string;
    stepId: string;
    actionId: string;
    value: StepWarningDetails;
};

export type StepWarningWithDetails = {
    key: string;
    stepId: string;
    value: StepWarningDetails;
};

export type StepWarningDetails = {
    summary: string;
    details?: string;
};

export type StepWarning = {
    key: string;
    stepId: string;
    value: string | ReactNode;
};

export type GlobalWarning = {
    key: string;
    value: string | ReactNode;
};

export type WarningValuePair = {
    key: string;
    value: string | ReactNode;
};

export type ProcessWarning = ActionWarning | StepWarning | GlobalWarning | StepWarningWithDetails | ActionWarningWithDetails;

//#endregion
